import { jsx as _jsx } from "preact/jsx-runtime";
import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { useLocation } from 'preact-iso';
import { CompanyContext } from './companyContext';
import { useAuth } from '@/auth/useAuth';
export const CompanyProvider = ({ children }) => {
    const location = useLocation();
    const { allWorkspaces, userCompanyRoleSets, homeClientCompanyId } = useAuth();
    const [companyId, setCompanyId] = useState(null);
    const [company, setCompany] = useState(null);
    const [workspaces, setWorkspaces] = useState([]);
    const [authOrganizationId, setAuthOrganizationId] = useState(undefined);
    const [hasEnabledAuthConnections, setHasEnabledAuthConnections] = useState(false);
    const [currentWorkspaceId, setCurrentWorkspaceId] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const lastLoadedCompanyIdRef = useRef(null);
    useEffect(() => {
        try {
            const match = location?.path?.match(/\/company\/([^/]+)/);
            const id = match?.[1] || null;
            setCompanyId(id);
        }
        catch {
            setCompanyId(null);
        }
    }, [location?.path]);
    const load = async (id) => {
        // Prevent loading if we don't have auth data yet
        if (!allWorkspaces || !userCompanyRoleSets) {
            return;
        }
        setIsLoading(true);
        setError(null);
        setAuthOrganizationId(undefined); // Reset to undefined while loading new company
        try {
            // Filter workspaces from auth state instead of making API call
            const companyWorkspaces = (allWorkspaces || []).filter(w => w.ClientCompanyId === id);
            // Check if user has access to this company
            const hasAccessToCompany = userCompanyRoleSets?.some(rs => rs.ClientCompanyId === id);
            if (!hasAccessToCompany && id !== homeClientCompanyId) {
                setError('You do not have access to this company');
                setWorkspaces([]);
                setCompany(null);
                setCurrentWorkspaceId(null);
                lastLoadedCompanyIdRef.current = id;
                setIsLoading(false);
                return;
            }
            // Only fetch full company details if needed (e.g., for settings pages that need AuthOrganizationId)
            // For now, we'll set basic company info from workspace data
            setWorkspaces(companyWorkspaces);
            // Don't create a fake company object - components that need full company details should fetch it
            // CompanyProvider primarily provides companyId and workspaces for navigation
            setCompany(null);
            setAuthOrganizationId(undefined); // Will be set by CompanyBootstrap when needed
            setHasEnabledAuthConnections(false); // Will be set by CompanyBootstrap when needed
            setCurrentWorkspaceId(prev => (companyWorkspaces.some(w => w.Id === prev) ? prev : (companyWorkspaces[0]?.Id || null)));
            lastLoadedCompanyIdRef.current = id;
            // eslint-disable-next-line @typescript-eslint/no-explicit-any -- backend error shape not strictly typed
        }
        catch (e) {
            setError(e?.message || 'Failed to load company');
        }
        finally {
            setIsLoading(false);
        }
    };
    useEffect(() => {
        if (!companyId) {
            setCompany(null);
            setWorkspaces([]);
            setAuthOrganizationId(undefined);
            setHasEnabledAuthConnections(false);
            setCurrentWorkspaceId(null);
            lastLoadedCompanyIdRef.current = null;
            return;
        }
        // Don't load if we don't have auth data yet
        if (!allWorkspaces || !userCompanyRoleSets)
            return;
        // Only reload if company changed or if this is first load for this company
        if (lastLoadedCompanyIdRef.current !== companyId) {
            load(companyId);
        }
    }, [companyId, allWorkspaces, userCompanyRoleSets, homeClientCompanyId, load]);
    const refresh = async () => { if (companyId)
        await load(companyId); };
    const currentWorkspace = useMemo(() => workspaces.find(w => w.Id === currentWorkspaceId) || null, [workspaces, currentWorkspaceId]);
    const value = useMemo(() => ({
        companyId,
        company,
        workspaces,
        authOrganizationId,
        hasEnabledAuthConnections,
        currentWorkspaceId,
        currentWorkspace,
        isLoading,
        error,
        refresh,
        setCurrentWorkspace: (workspaceId) => setCurrentWorkspaceId(workspaceId),
        updateCompany: (updated) => {
            setCompany(updated);
            const divs = (updated.Workspaces || []);
            setWorkspaces(divs);
            setHasEnabledAuthConnections(updated.HasEnabledAuthConnections ?? false);
            setCurrentWorkspaceId(prev => (divs.some(w => w.Id === prev) ? prev : (divs[0]?.Id || null)));
        },
        addWorkspace: (ws) => {
            setWorkspaces(prev => {
                if (prev.some(p => p.Id === ws.Id))
                    return prev;
                return [...prev, ws];
            });
            setCompany(prev => (prev ? { ...prev, Workspaces: [...prev.Workspaces, ws] } : prev));
            setCurrentWorkspaceId(id => id ?? ws.Id);
        },
        updateWorkspace: (ws) => {
            setWorkspaces(prev => prev.map(p => (p.Id === ws.Id ? ws : p)));
            setCompany(prev => (prev ? { ...prev, Workspaces: prev.Workspaces.map(p => (p.Id === ws.Id ? ws : p)) } : prev));
        },
        removeWorkspace: (workspaceId) => {
            setWorkspaces(prev => prev.filter(p => p.Id !== workspaceId));
            setCompany(prev => (prev ? { ...prev, Workspaces: prev.Workspaces.filter(p => p.Id !== workspaceId) } : prev));
            setCurrentWorkspaceId(prev => (prev === workspaceId ? (workspaces.filter(w => w.Id !== workspaceId)[0]?.Id || null) : prev));
        },
    }), [companyId, company, workspaces, authOrganizationId, currentWorkspaceId, isLoading, error]);
    return _jsx(CompanyContext.Provider, { value: value, children: children });
};
